import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { SxProps } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useAtomValue } from 'jotai';
import React, { MouseEventHandler, useMemo } from 'react';
import { toast } from 'react-toastify';

import { EmailBasicDto, EmailSendModeDto, EmailThreadSummaryItemDto } from '@/api/generated';
import { mapAttachmentItemToNewEmailAttachment } from '@/components/emailWindow/components/attachments/model/NewEmailAttachment.model.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { ToastConfig } from '@/config/ToastConfig.util.ts';
import { AnnouncementAnchor } from '@/contexts/announcements/AnnouncementAnchor.comp.tsx';
import { AnnouncementAnchorKey } from '@/contexts/announcements/announcements.definitions.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { TextCutWithDotsStyles } from '@/styles/text.styles.ts';
import { WrapTextStyle } from '@/styles/WrapText.styles.ts';
import { Override } from '@/utils/Override.utilityType.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import {
  NEW_EMAIL_REDUCER_EVENT,
  NEW_EMAIL_VIEW_MODE,
  useNewEmailUpdateState,
} from '@/views/emails/context/NewEmails.ts';
import { alreadySentDraftsAtom } from '@/views/emails/model/alreadySentDrafts.atom.ts';
import { userIsSenderOrReceiver } from '@/views/emails/util/userIsSenderOrReceiver.util.ts';

type HandleCopyEmail = (toBeCopied: string) => MouseEventHandler<HTMLButtonElement>;

interface Props {
  email: EmailBasicDto;
  fullDetails: boolean;
  isThreadEmailsList?: boolean;
  openInFloatingMode?: boolean;
  showTopic?: boolean;
}

export const EmailSummary: React.FC<Props> = props => {
  const { email, fullDetails, isThreadEmailsList, openInFloatingMode, showTopic } = props;
  const { t } = useTranslationWithPrefix('views.emails.threadEmails.email.summary');
  const updateNewEmailState = useNewEmailUpdateState();
  const alreadySentDrafts = useAtomValue(alreadySentDraftsAtom);

  if (!email.isDraft || alreadySentDrafts.has(email.emailId)) {
    return <EmailSummaryInner email={email} fullDetails={fullDetails} showTopic={showTopic} />;
  }

  const handleEditDraftClick: MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    const attachments = email.attachments.map(it =>
      mapAttachmentItemToNewEmailAttachment(it, true),
    );
    updateNewEmailState({
      payload: {
        attachments,
        draftRequestId: crypto.randomUUID(),
        email: { ...email },
        emailId: email.emailId,
        mode: email.sendMode ?? EmailSendModeDto.New,
        threadId: email.threadId,
      },
      type: NEW_EMAIL_REDUCER_EVENT.OPEN_EXISTING_DRAFT,
    });
    if (openInFloatingMode) {
      updateNewEmailState({
        payload: { mode: NEW_EMAIL_VIEW_MODE.FLOATING_WINDOW },
        type: NEW_EMAIL_REDUCER_EVENT.CHANGE_WINDOW_MODE,
      });
    }
  };

  return (
    <Grid container sx={EmailSummarySx}>
      <Grid size="grow">
        <EmailSummaryInner email={email} fullDetails={fullDetails} showTopic={showTopic} />
      </Grid>
      {isThreadEmailsList && (
        <Grid size={3}>
          <Button onClick={handleEditDraftClick} variant="outlined">
            <Stack direction="row" spacing={1}>
              {t('editDraft')} <EditOutlinedIcon />
            </Stack>
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

const EmailSummarySx: SxProps = { overflowX: 'hidden', width: '100%' };

interface EmailSummaryInnerProps {
  email: EmailBasicDto | EmailThreadSummaryItemDto;
  fullDetails: boolean;
  showTopic?: boolean;
}

export const EmailSummaryInner: React.FC<EmailSummaryInnerProps> = ({
  email,
  fullDetails,
  showTopic,
}) => {
  const { isRead } = email;
  const user = useUserContext();
  const { t } = useTranslationWithPrefix('views.emails.threadEmails.email.summary');

  const handleCopyEmail: HandleCopyEmail = toBeCopied => async event => {
    event.stopPropagation();
    await navigator.clipboard.writeText(toBeCopied);
    toast(t('emailCopiedConfirmation'), ToastConfig.success);
  };

  const userIsSender = email.from === user.email;
  const ccList = (email as Partial<EmailBasicDto>)?.cc ?? [];
  const toList = (email as Partial<EmailBasicDto>)?.to ?? [];
  const shouldShowRecipientsAnnouncement = fullDetails && (toList.length > 0 || ccList.length);

  const recipientsContent = (
    <>
      {toList.length > 0 && (
        <EmailsInCategory
          emailsList={toList}
          handleCopyEmail={handleCopyEmail}
          isRead={isRead}
          label={t('to')}
        />
      )}
      {fullDetails && ccList.length > 0 && (
        <EmailsInCategory
          emailsList={ccList}
          handleCopyEmail={handleCopyEmail}
          isRead={isRead}
          label={t('cc')}
        />
      )}
    </>
  );

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography
          component="div"
          sx={TextCutWithDotsStyles}
          variant={isRead ? 'f12Regular' : 'f12Medium'}
        >
          {userIsSenderOrReceiver({ emailToVerify: email.from, user })}
        </Typography>
        {fullDetails && !userIsSender && email.from && (
          <EmailWithCopyTooltip email={email.from} handleCopyEmail={handleCopyEmail} />
        )}
      </Stack>
      {shouldShowRecipientsAnnouncement ? (
        <AnnouncementAnchor anchorKey={AnnouncementAnchorKey.EMAILS_COPY_RECIPIENTS}>
          <Box>{recipientsContent}</Box>
        </AnnouncementAnchor>
      ) : (
        recipientsContent
      )}
      <EmailSummaryInnerTopic email={email} showTopic={showTopic} />
    </Box>
  );
};

const EmailSummaryInnerSx: SxProps = {
  overflowWrap: 'break-word',
  whiteSpace: 'normal',
  wordWrap: 'break-word',
};

const EmailSummaryInnerTopic: React.FC<
  Omit<
    Override<
      Props,
      {
        email: EmailBasicDto | EmailThreadSummaryItemDto;
      }
    >,
    'fullDetails'
  >
> = ({ email, showTopic }) => {
  const { t } = useTranslationWithPrefix('views.emails.threadEmails.email.summary');
  const { isDraft, isRead, subject } = email;

  const sx = useMemo(() => ({ color: isRead ? 'inherit' : themeColors.accentColor }), [isRead]);

  const handleCopySubject: MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    void navigator.clipboard
      .writeText(subject)
      .then(() => toast(t('subjectCopiedConfirmation'), ToastConfig.success))
      .catch(() => toast(t('subjectCopyFailed'), ToastConfig.error));
  };

  if (!showTopic) {
    if (isDraft) {
      return (
        <Typography
          component="div"
          noWrap
          sx={[{ flexGrow: 0 }, ...(Array.isArray(DraftLabelSx) ? DraftLabelSx : [DraftLabelSx])]}
          variant="f12Regular"
        >
          [{t('draft')}]
        </Typography>
      );
    }
    return null;
  }

  return (
    <Typography component="div" sx={sx} variant={isRead ? 'f12Regular' : 'f12Medium'}>
      <Stack direction="row" spacing={isDraft ? 1 : 0}>
        <Typography
          component="div"
          noWrap
          sx={[{ flexGrow: 0 }, ...(Array.isArray(DraftLabelSx) ? DraftLabelSx : [DraftLabelSx])]}
          variant="f12Regular"
        >
          {isDraft && `[${t('draft')}]`}
        </Typography>
        <Box
          sx={[
            { flexBasis: 0, flexGrow: 1 },
            ...(Array.isArray(WrapTextStyle) ? WrapTextStyle : [WrapTextStyle]),
          ]}
        >
          {subject}
          {subject && (
            <Tooltip title={t('button.copySubject')}>
              <IconButton onClick={handleCopySubject} sx={CopySubjectButtonSx}>
                <ContentCopyOutlinedIcon sx={TooltipSx} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Stack>
    </Typography>
  );
};

const DraftLabelSx = { color: themeColors.orange };
const TooltipSx = { color: themeColors.accentColor, fontSize: '16px' };
const CopySubjectButtonSx = { ml: 0.5, p: 0.25, verticalAlign: 'middle' };

const EmailWithCopyTooltip = (params: { email: string; handleCopyEmail: HandleCopyEmail }) => {
  const { t } = useTranslationWithPrefix('views.emails.threadEmails.email.summary');
  const { email, handleCopyEmail } = params;

  return (
    <Tooltip title={`${t('button.copy')}: ${email}`}>
      <IconButton onClick={handleCopyEmail(email)}>
        <ContentCopyOutlinedIcon sx={TooltipSx} />
      </IconButton>
    </Tooltip>
  );
};

const EmailWithCopyIcon = (params: { email: string; handleCopyEmail: HandleCopyEmail }) => {
  const user = useUserContext();
  const { email, handleCopyEmail } = params;
  return (
    <Stack direction="row" key={email} sx={{ alignItems: 'center' }}>
      {userIsSenderOrReceiver({ emailToVerify: email, user })}
      <EmailWithCopyTooltip email={email} handleCopyEmail={handleCopyEmail} />
    </Stack>
  );
};

const EmailsInCategory = (params: {
  emailsList: string[];
  handleCopyEmail: HandleCopyEmail;
  isRead: boolean;
  label: string;
}) => {
  const { emailsList, handleCopyEmail, isRead, label } = params;
  return (
    <Typography
      component="div"
      sx={EmailSummaryInnerSx}
      variant={isRead ? 'f12Regular' : 'f12Medium'}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        {label}:
        {emailsList.map(it => (
          <EmailWithCopyIcon email={it} handleCopyEmail={handleCopyEmail} key={it} />
        ))}
      </Stack>
    </Typography>
  );
};
